/**
 * uPlot chart setup for the frequency response display.
 *
 * Log-frequency X-axis (20 Hz – 20 kHz), linear dB Y-axis.
 * Series: live spectrum, averaged spectrum, target curve, EQ curve.
 */

import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";

export interface ChartSeries {
  /** Frequency values (shared X axis) */
  frequencies: number[];
  /** Live spectrum dB values */
  live: Float64Array | null;
  /** Time-averaged spectrum dB values */
  averaged: Float64Array | null;
  /** Target curve (inverse C-weighting) dB values */
  target: Float64Array;
  /** Fitted EQ correction curve dB values */
  eqCurve: Float64Array | null;
  /** Predicted response (measured + EQ) */
  predicted: Float64Array | null;
}

const COLORS = {
  live: "rgba(100, 180, 255, 0.35)",
  averaged: "rgba(100, 180, 255, 1)",
  target: "rgba(255, 200, 60, 0.8)",
  eqCurve: "rgba(120, 255, 120, 0.8)",
  predicted: "rgba(255, 120, 255, 0.7)",
};

/**
 * Format a frequency value for axis labels.
 */
function formatFreq(f: number): string {
  if (f >= 1000) return (f / 1000).toFixed(f >= 10000 ? 0 : 1) + "k";
  return Math.round(f).toString();
}

/**
 * Generate log-spaced tick values for the frequency axis.
 */
function freqTicks(): number[] {
  const ticks = [20, 31.5, 50, 63, 100, 125, 200, 250, 500, 1000, 2000, 4000, 8000, 10000, 16000, 20000];
  return ticks;
}

/**
 * Create the uPlot chart instance.
 */
export function createChart(
  container: HTMLElement,
  frequencies: number[],
  targetCurve: Float64Array
): uPlot {
  // Convert frequencies to log scale for even spacing on the X axis
  const logFreqs = frequencies.map((f) => Math.log10(f));

  const opts: uPlot.Options = {
    width: container.clientWidth,
    height: Math.min(500, container.clientHeight || 500),
    class: "sound-meter-chart",
    cursor: {
      show: true,
      x: true,
      y: true,
    },
    legend: {
      show: true,
    },
    scales: {
      x: {
        time: false,
        auto: false,
        range: [Math.log10(20), Math.log10(20000)],
      },
      y: {
        auto: true,
        range: (_u: uPlot, min: number, max: number) => {
          // Clamp to reasonable dBFS range, pad by 5 dB
          const lo = Math.max(-120, Math.floor((min - 5) / 10) * 10);
          const hi = Math.min(10, Math.ceil((max + 5) / 10) * 10);
          return [lo, hi];
        },
      },
    },
    axes: [
      {
        label: "Frequency (Hz)",
        values: (_u: uPlot, vals: number[]) =>
          vals.map((v) => formatFreq(Math.pow(10, v))),
        splits: () => freqTicks().map((f) => Math.log10(f)),
        grid: { show: true, stroke: "rgba(255,255,255,0.08)" },
        ticks: { show: true, stroke: "rgba(255,255,255,0.15)" },
        stroke: "rgba(255,255,255,0.5)",
        font: "11px monospace",
      },
      {
        label: "dB",
        grid: { show: true, stroke: "rgba(255,255,255,0.08)" },
        ticks: { show: true, stroke: "rgba(255,255,255,0.15)" },
        stroke: "rgba(255,255,255,0.5)",
        font: "11px monospace",
      },
    ],
    series: [
      {
        // X axis (log frequency)
        label: "Freq",
      },
      {
        label: "Live",
        stroke: COLORS.live,
        width: 1,
        points: { show: false },
      },
      {
        label: "Averaged",
        stroke: COLORS.averaged,
        width: 2,
        points: { show: false },
      },
      {
        label: "C-Curve (dB(C))",
        stroke: COLORS.target,
        width: 2,
        dash: [6, 3],
        points: { show: false },
      },
      {
        label: "EQ Curve",
        stroke: COLORS.eqCurve,
        width: 2,
        dash: [4, 4],
        points: { show: false },
      },
      {
        label: "Predicted",
        stroke: COLORS.predicted,
        width: 1.5,
        dash: [2, 2],
        points: { show: false },
      },
    ],
  };

  // Initial data: frequencies + target curve, others null
  const nullArray = new Array(logFreqs.length).fill(null);
  const targetArray = Array.from(targetCurve);

  const data: uPlot.AlignedData = [
    logFreqs,      // X
    nullArray,      // live
    nullArray,      // averaged
    targetArray,    // target
    nullArray,      // eqCurve
    nullArray,      // predicted
  ];

  const chart = new uPlot(opts, data, container);

  // Handle resize
  const observer = new ResizeObserver(() => {
    chart.setSize({
      width: container.clientWidth,
      height: Math.min(500, container.clientHeight || 500),
    });
  });
  observer.observe(container);

  return chart;
}

/**
 * Update chart data with new series values.
 */
export function updateChart(
  chart: uPlot,
  logFreqs: number[],
  series: ChartSeries
): void {
  const toArr = (data: Float64Array | null): (number | null)[] => {
    if (!data) return new Array(logFreqs.length).fill(null);
    return Array.from(data) as (number | null)[];
  };

  chart.setData([
    logFreqs,
    toArr(series.live),
    toArr(series.averaged),
    Array.from(series.target) as (number | null)[],
    toArr(series.eqCurve),
    toArr(series.predicted),
  ]);
}
